import type { ValidationError } from '../../shared-ui-layer/types/validation.types'

export interface SourceInfo {
  appSource: 'communities' | 'courses'
  bucket: string
  filePath: string
}

export interface MediaAttachment {
  id: string
  type: string
  url?: string
  html?: string
  thumbnail_url?: string
  licenseId?: string
  sourceInfo?: SourceInfo
  blurhash?: string
  blurhashCode?: string
}

/**
 * Numeric width/height ratios used by the image cropping UI (e.g. cropper
 * presets). These are JS numbers, not wire labels.
 *
 * NOT the same thing as the canonical "W:H" string labels emitted on media
 * payloads — those live in `CANONICAL_ASPECT_RATIOS` in
 * `utils/dimensions.utils.ts` and are what Flutter / image buckets consume.
 * Use this enum only for cropper math; use `CanonicalAspectRatio` strings
 * for anything that crosses the wire.
 */
export enum AspectRatio {
  SQUARE = 1, // 1:1
  LANDSCAPE_16_9 = 16 / 9, // 16:9
  PORTRAIT_9_16 = 9 / 16, // 9:16
  LANDSCAPE_4_3 = 4 / 3, // 4:3
  PORTRAIT_3_4 = 3 / 4, // 3:4
  LANDSCAPE_3_2 = 3 / 2, // 3:2
  PORTRAIT_2_3 = 2 / 3, // 2:3
  LANDSCAPE_2_1 = 2, // 2:1 — exhibitor logos are 400×200
}

/**
 * Supported image output formats for cropped images
 */
export enum ImageOutputFormat {
  WEBP = 'webp', // WebP format (default, modern, efficient)
  PNG = 'png', // PNG format (lossless)
  JPEG = 'jpeg', // JPEG format (lossy, smaller file size)
}

/**
 * Supported 3rd-party embed sources.
 * Mirrors EMBED_SOURCE from courses/types-layer for use in media-layer.
 */
export enum EmbedSource {
  YOUTUBE = 'youtube',
  VIMEO = 'vimeo',
  LOOM = 'loom',
  WISTIA = 'wistia',
  SOUNDCLOUD = 'soundcloud',
  SPOTIFY = 'spotify',
  MIXCLOUD = 'mixcloud',
}

/**
 * Content type classification for embed sources.
 */
export enum EmbedContentType {
  AUDIO = 'audio',
  VIDEO = 'video',
}

/**
 * Platforms classified as audio embeds.
 */
export const AUDIO_EMBED_PLATFORMS: ReadonlySet<string> = new Set([EmbedSource.SPOTIFY, EmbedSource.SOUNDCLOUD, EmbedSource.MIXCLOUD])

/**
 * Platforms classified as video embeds.
 */
export const VIDEO_EMBED_PLATFORMS: ReadonlySet<string> = new Set([
  EmbedSource.YOUTUBE,
  EmbedSource.VIMEO,
  EmbedSource.LOOM,
  EmbedSource.WISTIA,
])

/**
 * Union type for embed platforms — includes all known sources plus 'custom' fallback
 */
export type EmbedPlatform = EmbedSource | 'custom'

/**
 * Parsed embed info returned by getEmbedInfo()
 */
export interface EmbedInfo {
  platform: EmbedPlatform
  contentType?: EmbedContentType
  embedUrl?: string
  thumbnailUrl?: string
  contentId?: string
}

/** Vimeo-specific fields for unlisted/private links (`vimeo.com/{id}/{hash}`). */
export interface VimeoEmbedInfo extends EmbedInfo {
  platform: EmbedSource.VIMEO
  privacyHash?: string
}

export function isVimeoEmbedInfo(info: EmbedInfo): info is VimeoEmbedInfo {
  return info.platform === EmbedSource.VIMEO
}

/**
 * Display metadata for a link embed, resolved from the provider's oEmbed endpoint.
 */
export interface LinkEmbedPreview {
  title?: string
  authorName?: string
  providerName?: string
  thumbnailUrl?: string
}

/** Vimeo-specific preview fields for tests/mocks that need to simulate hash-bearing preview state. */
export interface VimeoLinkEmbedPreview extends LinkEmbedPreview {
  privacyHash?: string
}

export interface PlayerStyles {
  controlsBgColor: string
  primaryColor: string
  borderRadius: string
  menuBgColor: string
  textColor: string
  volumeBgColor: string
  videoBgColor?: string
}

// ===== DRM Image Types =====

/**
 * Image format types supported by Assets DRM
 */
export type ImageFormat = 'jpg' | 'png' | 'webp' | 'avif' | 'gif' | 'tiff' | 'heif' | 'jp2' | 'jxl'

/**
 * Resize modes for image transformation
 * - fit: Resize to fit within dimensions, maintains aspect ratio
 * - fill: Fill the entire dimensions, may crop parts
 * - fill-down: Same as fill, but crops to keep aspect ratio if smaller
 * - force: Resize without maintaining aspect ratio
 * - auto: Use fill if same orientation, otherwise fit
 */
export type ResizeMode = 'fit' | 'fill' | 'fill-down' | 'force' | 'auto'

/**
 * Gravity positions for cropping
 */
export type GravityPosition = 'ce' | 'no' | 'so' | 'ea' | 'we' | 'noea' | 'nowe' | 'soea' | 'sowe'

/**
 * Options for generating DRM image URLs
 */
export interface DRMImageOptions {
  /** Asset license ID (if available) */
  licenseId?: string

  /** Original image source URL (fallback if no licenseId) */
  src?: string

  /**
   * App source identifier (default: 'communities')
   * - For licenseId: Required (specifies DRM path)
   * - For GCS URLs: Optional (auto-detected from bucket name)
   */
  source?: 'communities' | 'courses' | 'client-portal' | 'gokollab' | 'events-management' | string

  /** Image width in pixels */
  width?: number

  /** Image height in pixels */
  height?: number

  /** Output format (default: 'webp') */
  format?: ImageFormat

  /** Image quality 1-100 (default: 90) */
  quality?: number

  /** Resize mode (default: 'fill') */
  resize?: ResizeMode

  /** Gravity/crop position (default: 'ce' for center) */
  gravity?: GravityPosition

  /** Device pixel ratio (default: 1) */
  dpr?: number

  /** Blur effect 0-10 */
  blur?: number

  /** Sharpen effect 0-4 */
  sharpen?: number

  /** Pixelate effect 0-20 */
  pixelate?: number

  /** Rotation in degrees */
  rotate?: number

  /** Background color in RGB format (e.g., '255:255:255') */
  bgColor?: string

  /** Padding in format 'top:right:bottom:left' (e.g., '10:10:10:10') */
  padding?: string

  /** Maximum file size in bytes */
  maxBytes?: number
}

// ===== URL Types =====

/**
 * CDN to bucket mapping for DRM transformation
 */
export interface CDNMapping {
  hostname: string
  bucket: string
  environment: 'production' | 'staging'
}

// ===== Media Dimension Fields =====

/**
 * Flat dimension fields attached to uploaded/imported media on the wire.
 * `aspectRatio` is a canonical "W:H" string (e.g. "16:9") snapped to one of CANONICAL_ASPECT_RATIOS.
 * All fields are optional so adopters can progressively populate them
 * without breaking existing consumers.
 */
export interface MediaDimensionFields {
  width?: number
  height?: number
  aspectRatio?: string
}

// ===== oEmbed Types =====

/**
 * Standard oEmbed fields returned by provider APIs (YouTube, Vimeo, Loom).
 * Mix this into any media-item or attachment interface that stores imported videos.
 *
 * `aspectRatio` is derived from `width`/`height` by `applyOEmbedMeta` and
 * matches the canonical "W:H" label from CANONICAL_ASPECT_RATIOS.
 */
export interface OEmbedFields {
  html?: string
  thumbnail_url?: string
  thumbnail_width?: number
  thumbnail_height?: number
  provider_name?: string
  provider_url?: string
  author_name?: string
  author_url?: string
  title?: string
  width?: number
  height?: number
  aspectRatio?: string
}

// ===== Media Helpers Types =====

/**
 * Generic media item interface that both MediaAttachment and FileUpload satisfy
 */
export interface MediaItem {
  url?: string
  html?: string
  thumbnail_url?: string
  type?: string
  licenseId?: string
  sourceInfo?: SourceInfo
}

// ===== File Validation Types =====

export interface ImageValidation {
  maxFiles?: number
  maxFileSize?: number
  minFileSize?: number
  acceptedTypes?: string[]
  maxDimensions?: {
    width: number
    height: number
  }
  minDimensions?: {
    width: number
    height: number
  }
}

export interface VideoValidation {
  maxFiles?: number
  maxFileSize?: number
  minFileSize?: number
  acceptedTypes?: string[]
  maxDuration?: number
  minDuration?: number
}

export interface FileUploadValidation {
  maxFiles?: number
  maxFileSize?: number
  minFileSize?: number
  acceptedTypes?: string[]
  customValidator?: (file: File) => {
    valid: boolean
    error?: string
  }
  onValidationError?: (errors: ValidationError[]) => void
}

// ===== DRM MediaPicker integration types =====

export interface DRMConfig {
  /** Authentication token */
  token: string
  /** App source identifier (e.g., 'communities', 'courses') */
  source: string
  /** App source identifier (same as source or more specific) */
  appSource: string
  /** Higher level entity type that owns the asset */
  entityType: string
  /** Unique identifier for the entity that will own the asset */
  entityId: string
  /** Lower level entity type that the asset is associated with */
  sourceEntityType: string
  /** Unique identifier for the source entity */
  sourceEntityId: string
}

export interface DRMUploadError {
  /** Error type */
  type: 'drm' | 'regular'
  /** Error message */
  message: string
  /** File that failed to upload */
  file: File
  /** Additional error metadata */
  metadata?: Record<string, unknown>
}

export interface DRMUploadResult {
  /** License ID for the uploaded asset */
  licenseId: string
  /** File that was uploaded */
  file: File
  /** Upload type */
  type: 'image' | 'video'
  /** Additional result metadata */
  metadata?: Record<string, unknown>
}

// ===== DRM Prefetch Types =====

export interface PrefetchSourceInfo {
  bucket: string
  filePath: string
}

export type PrefetchAssetType = 'video' | 'image'

export interface PrefetchUrl {
  url: string
  assetType: PrefetchAssetType
}

export interface PrefetchIdentifiers {
  licenseIds?: string[]
  sourceInfos?: PrefetchSourceInfo[]
  urls?: PrefetchUrl[]
}

/**
 * Generic interfaces for media-bearing objects used by prefetch extractors.
 * Kept minimal so any domain's Post/Lesson type satisfies them without coupling.
 */
export interface PostWithAttachments {
  metadata?: { licenseId?: string }
  attachments?: Array<{ licenseId?: string; url?: string; type?: string }>
}

export interface LessonWithMedia {
  video?: { assetsLicenseId?: string; url?: string }
  audio?: { assetsLicenseId?: string }
}
